import { IntegrationsAgreggatedSyncableStateValuesEnum } from 'generated/resources/datatable.graphql'
import {
  CompensationsValueObjectsEmployeeCompensationValidationTypeEnum,
  CompensationsValueObjectsEmployeeStatusEnum,
} from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'

import { EmployeePayrollRunNode } from 'modules/compensations/components/EmployeePayrollRunElements'
import { VALID_EMPLOYEE_STATUSES } from 'modules/compensations/types/filters'

export type RendererDefinition =
  | { type: 'text'; value: string }
  | { type: 'status'; value: { status: string; label: string } }

export interface ValidationFiltersDataResult {
  aboveLimit?: { totalCount?: number | null } | null
  pendingToDefine?: { totalCount?: number | null } | null
  discrepancy?: { totalCount?: number | null } | null
}

function countAlerts(item: EmployeePayrollRunNode): number {
  const compensationsWithValidations = item.compensations.filter(
    (compensation) => compensation.validations && compensation.validations.length > 0
  )

  return compensationsWithValidations.length
}

export function createCountItemsWithAlerts(
  validationFiltersDataResult?: ValidationFiltersDataResult | null
) {
  return (validationType: string) => {
    return () => {
      if (!validationFiltersDataResult) return 0

      if (validationType === 'above_limit') {
        return validationFiltersDataResult.aboveLimit?.totalCount ?? 0
      }

      if (validationType === 'pending_to_define') {
        return validationFiltersDataResult.pendingToDefine?.totalCount ?? 0
      }

      if (validationType === 'discrepancy') {
        return validationFiltersDataResult.discrepancy?.totalCount ?? 0
      }

      return 0
    }
  }
}

export function calculateTotalAlerts(
  validationFiltersDataResult?: ValidationFiltersDataResult | null
): number {
  const countItemsWithAlerts = createCountItemsWithAlerts(validationFiltersDataResult)

  return (
    countItemsWithAlerts('above_limit')() +
    countItemsWithAlerts('pending_to_define')() +
    countItemsWithAlerts('discrepancy')()
  )
}

export function renderEmployeeColumn(
  item: EmployeePayrollRunNode
) {
  const firstName = item.employee?.firstName ?? ''
  const lastName = item.employee?.lastName ?? ''
  const name = [firstName, lastName].filter(Boolean).join(' ')
  return {
    type: 'person' as const,
    value: {
      firstName,
      lastName,
      name,
      deactivated: !item.employee?.active,
      src: buildUrl(item.employee?.avatar?.url ?? '') ?? '',
    },
  }
}

export function renderAlertsColumn(item: EmployeePayrollRunNode): RendererDefinition {
  const alertCount = countAlerts(item)

  if (alertCount > 0) {
    return {
      type: 'status',
      value: {
        status: 'warning',
        label: String(alertCount),
      },
    }
  }

  return {
    type: 'text',
    value: '-',
  }
}

type CollectionFilters = {
  legalEntityIds?: unknown
  teamIds?: unknown
  workplaceIds?: unknown
  validationTypes?: unknown
  employeeStatus?: unknown
  integrationsAgreggatedSyncableStates?: unknown
}

export type ProcessedFilters = {
  legalEntityIds?: number[]
  teamIds?: number[]
  workplaceIds?: number[]
  validationTypes?: CompensationsValueObjectsEmployeeCompensationValidationTypeEnum[]
  employeeStatus?: CompensationsValueObjectsEmployeeStatusEnum[]
  integrationsAgreggatedSyncableStates?: IntegrationsAgreggatedSyncableStateValuesEnum[]
}

function isValidValidationType(
  type: string
): type is CompensationsValueObjectsEmployeeCompensationValidationTypeEnum {
  return type === 'above_limit' || type === 'pending_to_define' || type === 'discrepancy'
}

const validEmployeeStatusSet = new Set<string>(VALID_EMPLOYEE_STATUSES)
function isValidEmployeeStatus(
  status: string
): status is CompensationsValueObjectsEmployeeStatusEnum {
  return validEmployeeStatusSet.has(status)
}

function createIsValidSyncableStateType(
  validSyncStateValues: IntegrationsAgreggatedSyncableStateValuesEnum[]
) {
  return (type: unknown): type is IntegrationsAgreggatedSyncableStateValuesEnum => {
    if (typeof type !== 'string') {
      return false
    }

    return validSyncStateValues.some((validValue) => validValue === type)
  }
}

export function processCompensationsFilters(
  collectionFilters?: CollectionFilters,
  validSyncStateValues?: IntegrationsAgreggatedSyncableStateValuesEnum[]
): ProcessedFilters {
  const legalEntityIds = Array.isArray(collectionFilters?.legalEntityIds)
    ? collectionFilters.legalEntityIds.map(Number).filter(Boolean)
    : undefined

  const teamIds = Array.isArray(collectionFilters?.teamIds)
    ? collectionFilters.teamIds.map(Number).filter(Boolean)
    : undefined

  const workplaceIds = Array.isArray(collectionFilters?.workplaceIds)
    ? collectionFilters.workplaceIds.map(Number).filter(Boolean)
    : undefined

  const validationTypes:
    | CompensationsValueObjectsEmployeeCompensationValidationTypeEnum[]
    | undefined = Array.isArray(collectionFilters?.validationTypes)
    ? collectionFilters.validationTypes
        .filter((type): type is string => typeof type === 'string' && Boolean(type))
        .filter(isValidValidationType)
    : undefined

  const employeeStatusRaw = collectionFilters?.employeeStatus
  const employeeStatusFiltered = Array.isArray(employeeStatusRaw)
    ? employeeStatusRaw
        .filter((status): status is string => typeof status === 'string' && Boolean(status))
        .filter(isValidEmployeeStatus)
    : []
  const employeeStatus = employeeStatusFiltered.length > 0 ? employeeStatusFiltered : undefined

  let integrationsAgreggatedSyncableStates:
    | IntegrationsAgreggatedSyncableStateValuesEnum[]
    | undefined = undefined

  if (validSyncStateValues && validSyncStateValues.length > 0) {
    const isValidSyncableStateType = createIsValidSyncableStateType(validSyncStateValues)

    const syncStates =
      'integrationsAgreggatedSyncableStates' in (collectionFilters ?? {}) &&
      Array.isArray(collectionFilters?.integrationsAgreggatedSyncableStates)
        ? collectionFilters.integrationsAgreggatedSyncableStates
        : undefined

    if (syncStates) {
      const validStates = syncStates.filter(isValidSyncableStateType)
      integrationsAgreggatedSyncableStates = validStates.length > 0 ? validStates : undefined
    }
  }

  return {
    legalEntityIds: legalEntityIds && legalEntityIds.length > 0 ? legalEntityIds : undefined,
    teamIds: teamIds && teamIds.length > 0 ? teamIds : undefined,
    workplaceIds: workplaceIds && workplaceIds.length > 0 ? workplaceIds : undefined,
    validationTypes,
    employeeStatus,
    integrationsAgreggatedSyncableStates,
  }
}
